// garuda - Inks (Writeups) Page

import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useNavigation } from "../context/NavigationContext";
import { AmpersandBg, ShareIcon } from "../components/Icons";
import MarkdownRenderer from "../components/MarkdownRenderer";
import { formatTimestamp, copyToClipboard } from "../utils/helpers";
import JackpotWord from "../components/JackpotWord";
import { fetchAllContent } from "../services/contentService";
import PageNavigation from "../components/PageNavigation";
import "./Inks.css";

const Inks = () => {
  const { slug: urlSlug } = useParams();
  const { navigateTo } = useNavigation();
  const [inks, setInks] = useState([]);
  const [filteredInks, setFilteredInks] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedInk, setSelectedInk] = useState(null);
  const [isReadingMode, setIsReadingMode] = useState(true);
  const [loading, setLoading] = useState(true);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [fromUrl, setFromUrl] = useState(false);

  // Load content from files on mount
  useEffect(() => {
    const loadInks = async () => {
      setLoading(true);
      const content = await fetchAllContent("inks");
      setInks(content);
      setFilteredInks(content);
      setLoading(false);

      // If URL has a slug, find and select that ink
      if (urlSlug && content.length > 0) {
        const ink = content.find((i) => i.id === urlSlug);
        if (ink) {
          setSelectedInk(ink);
          setFromUrl(true); // Mark that we came from URL
        }
      }
    };
    loadInks();
  }, [urlSlug]);

  // Search functionality
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredInks(inks);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = inks.filter(
      (ink) =>
        ink.title.toLowerCase().includes(query) ||
        (ink.tags && ink.tags.some((tag) => tag.toLowerCase().includes(query))),
    );
    setFilteredInks(filtered);
  }, [searchQuery, inks]);

  const handleInkClick = (ink) => {
    setSelectedInk(ink);
    setFromUrl(false); // Mark as internal navigation
  };

  const handleBack = () => {
    if (fromUrl) {
      // If we came from URL, navigate back to home
      window.location.href = "/";
    } else {
      // If we navigated internally, just clear state
      setSelectedInk(null);
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/inks/${selectedInk.id}`;
    const success = await copyToClipboard(url);

    if (success) {
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    } else {
      console.error("Failed to copy URL");
    }
  };

  const toggleReadingMode = () => {
    setIsReadingMode(!isReadingMode);
  };

  // Apply reading mode class to body
  useEffect(() => {
    if (isReadingMode) {
      document.body.classList.add("reading-mode");
    } else {
      document.body.classList.remove("reading-mode");
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove("reading-mode");
    };
  }, [isReadingMode]);

  // If viewing a specific ink, show reading view
  if (selectedInk) {
    return (
      <div className="inks-container reading-view">
        <PageNavigation
          isReadingMode={isReadingMode}
          onToggleReadingMode={toggleReadingMode}
          showReadingMode={true}
        />
        <AmpersandBg opacity={0.03} />

        <div className="reading-header">
          <button className="back-button" onClick={handleBack}>
            ← Back to Inks
          </button>
        </div>

        <article className="reading-content">
          <div className="reading-title-row">
            <h1 className="reading-title">{selectedInk.title}</h1>
            <button
              className="share-button"
              onClick={handleShare}
              title="Copy link to clipboard"
            >
              <ShareIcon />
              {copyFeedback && <span className="copy-feedback">Copied!</span>}
            </button>
          </div>
          <div className="author-section">
            <img
              src="/images/profile.jpg"
              alt="Prem Agarwal"
              className="author-avatar"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
            <div
              className="author-avatar author-avatar-fallback"
              style={{ display: "none" }}
            >
              PA
            </div>
            <div className="author-info">
              <div className="author-name">Prem Agarwal</div>
              <div className="author-timestamp">
                {formatTimestamp(selectedInk.timestamp)}
              </div>
            </div>
          </div>
          {selectedInk.tags && selectedInk.tags.length > 0 && (
            <div className="reading-tags">
              {selectedInk.tags.map((tag, idx) => (
                <span key={idx} className="tag">
                  #{tag}
                </span>
              ))}
            </div>
          )}
          <MarkdownRenderer content={selectedInk.content} />
        </article>
      </div>
    );
  }

  // Default: Show grid view
  return (
    <div className="inks-container">
      <PageNavigation />
      <AmpersandBg opacity={0.05} />

      <div className="inks-header">
        <h1 className="page-title">
          <JackpotWord onClick={() => navigateTo("home")}>Inks</JackpotWord>
        </h1>
        <p className="page-subtitle">
          Deep dives into the technical and philosophical.
        </p>
      </div>

      {/* Search Bar */}
      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder="search writeups_"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="inks-grid">
        {loading ? (
          <p className="no-results">Loading writeups...</p>
        ) : filteredInks.length === 0 ? (
          <p className="no-results">No writeups found.</p>
        ) : (
          filteredInks.map((ink) => (
            <div
              key={ink.id}
              className="ink-card"
              onClick={() => handleInkClick(ink)}
            >
              <div className="ink-card-header">
                <h2 className="ink-card-title">
                  <JackpotWord>{ink.title}</JackpotWord>
                </h2>
              </div>
              <p className="ink-card-timestamp">
                {formatTimestamp(ink.timestamp)}
              </p>
              {ink.tags && ink.tags.length > 0 && (
                <div className="ink-card-tags">
                  {ink.tags.map((tag, idx) => (
                    <span key={idx} className="tag">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Inks;
